import { useCallback, useMemo } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { YtgService } from "@modules/ytg/api/ytg.service";
import { parseUTC } from "@utils/dateHelpers";
import type { SerpItem } from "@shared-types";

interface RowDateResponse {
    date?: string | null;
    keyword?: string | null;
    [key: string]: unknown;
}

type RowScoreResponse = Record<string, unknown>;

/** Return shape of {@link useYtgRowDetails}. */
export interface UseYtgRowDetailsReturn {
    serp: SerpItem[];
    date: string | null;
    keyword: string | null;
    score: RowScoreResponse | null;
    loading: boolean;
    error: Error | null;
    /** Invalidate the row's SERP, date, and score queries to force a refresh. */
    refetch: () => void;
}

/**
 * Loads SERP, date, and score for a row in parallel via 3 independent queries.
 *
 * Each sub-query is cached on its own queryKey, so revisiting the same row
 * after navigating away returns instantly from cache while a background
 * refresh happens.
 */
export const useYtgRowDetails = (
    rowId: number | string | null | undefined,
): UseYtgRowDetailsReturn => {
    const enabled = Boolean(rowId);
    const queryClient = useQueryClient();

    const results = useQueries({
        queries: [
            {
                queryKey: ["ytg", "row-serp", rowId],
                queryFn: ({ signal }: { signal: AbortSignal }) =>
                    YtgService.getSerpByRowId(rowId!, { signal }),
                enabled,
            },
            {
                queryKey: ["ytg", "row-date", rowId],
                queryFn: ({ signal }: { signal: AbortSignal }) =>
                    YtgService.getRowDate(rowId!, { signal }),
                enabled,
            },
            {
                queryKey: ["ytg", "row-score", rowId],
                queryFn: ({ signal }: { signal: AbortSignal }) =>
                    YtgService.getRowScore(rowId!, { signal }),
                enabled,
            },
        ],
    });

    const [serpQ, dateQ, scoreQ] = results;
    const loading = enabled && results.some((q) => q.isLoading);
    const allFailed = enabled && results.every((q) => q.isError);
    const error = allFailed ? new Error("Toutes les requêtes ont échoué") : null;

    const dateData: RowDateResponse | null = (dateQ.data as RowDateResponse | undefined) ?? null;
    const formattedDate = useMemo<string | null>(() => {
        if (!dateData?.date) return null;
        try {
            return parseUTC(dateData.date)?.toLocaleDateString("fr-FR") ?? null;
        } catch {
            return dateData.date ?? null;
        }
    }, [dateData]);

    const refetch = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ["ytg", "row-serp", rowId] });
        queryClient.invalidateQueries({ queryKey: ["ytg", "row-date", rowId] });
        queryClient.invalidateQueries({ queryKey: ["ytg", "row-score", rowId] });
    }, [queryClient, rowId]);

    return {
        serp: serpQ.data ?? [],
        date: formattedDate,
        keyword: dateData?.keyword ?? null,
        score: (scoreQ.data ?? null) as RowScoreResponse | null,
        loading,
        error,
        refetch,
    };
};
